import type NativeArray from '@ember/array/-private/native-array';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed, get } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type { AsyncBelongsTo, AsyncHasMany, SyncHasMany } from '@ember-data/model';
import { attr, belongsTo, hasMany } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { apiAction } from '@mainmatter/ember-api-actions';
import isRemovedString from 'garaje/helpers/is-removed-string';
import type { AgreementsStatus } from 'garaje/models/agreeable';
import Agreeable from 'garaje/models/agreeable';
import type AgreeableNdaModel from 'garaje/models/agreeable-nda';
import type { ReportSource } from 'garaje/models/approval-status';
import type AttendeeModel from 'garaje/models/attendee';
import type EmployeeModel from 'garaje/models/employee';
import type EntryModel from 'garaje/models/entry';
import type FlowModel from 'garaje/models/flow';
import type FlowVersionModel from 'garaje/models/flow-version';
import type GroupInviteModel from 'garaje/models/group-invite';
import type LocationModel from 'garaje/models/location';
import type NotificationLogModel from 'garaje/models/notification-log';
import type ParentInviteContextModel from 'garaje/models/parent-invite-context';
import type PlatformJobModel from 'garaje/models/platform-job';
import type QuestionnaireResponse from 'garaje/models/questionnaire-response';
import type QuestionnaireResponseSummary from 'garaje/models/questionnaire-response-summary';
import type RecurringInviteModel from 'garaje/models/recurring-invite';
import type ReservationModel from 'garaje/models/reservation';
import type SignedAgreementsJobModel from 'garaje/models/signed-agreements-job';
import type UserModel from 'garaje/models/user';
import type UserDatum from 'garaje/models/user-datum';
import type UserDocumentTemplateModel from 'garaje/models/user-document-template';
import type { Identifier } from 'garaje/models/user-document-template';
import type VisitApproval from 'garaje/models/visit-approval';
import type { ApprovalStatus } from 'garaje/models/visit-approval';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentLocationService from 'garaje/services/current-location';
import { extractPhoneNumber } from 'garaje/utils/blocklist-utils';
import {
  needsLocationApprovalReview,
  needsPropertyApprovalReview,
} from 'garaje/utils/check-blocklist-approval-requirements';
import { FLOW_TYPE } from 'garaje/utils/enums';
import type PlainObject from 'garaje/utils/plain-object';
import type { RecordArray } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import type { SingleResponse } from 'jsonapi/response';
import { alias, not, filterBy, sortBy } from 'macro-decorators';

export interface UserDataValidationResult {
  isValid: boolean;
  errors: Array<{ field: string; message: string }>;
}

const VALID_USER_DATA_NO_ERRORS: UserDataValidationResult = Object.freeze({
  isValid: true,
  errors: [],
});

export default class Invite extends Agreeable {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentLocation: CurrentLocationService;
  @service declare store: StoreService;

  @belongsTo('attendee', { async: true, inverse: 'invite' }) declare attendee: AsyncBelongsTo<AttendeeModel>;
  @belongsTo('user', { async: true, inverse: null }) declare creator: AsyncBelongsTo<UserModel>;
  @belongsTo('entry', { async: true, inverse: 'invite' }) declare entry: AsyncBelongsTo<EntryModel> | null;
  @belongsTo('employee', { async: true, inverse: 'invites' }) declare employee: AsyncBelongsTo<EmployeeModel> | null;
  @belongsTo('location', { async: true, inverse: 'invites' }) declare location: AsyncBelongsTo<LocationModel>;
  @belongsTo('recurring-invite', { async: true, inverse: null })
  declare recurringInvite: AsyncBelongsTo<RecurringInviteModel>;
  @belongsTo('signed-agreements-job', { async: true, inverse: 'invite' })
  declare signedAgreementsJob: AsyncBelongsTo<SignedAgreementsJobModel>;
  /**
   * Read only. API does not actually read from this field to update the flow. UI might still update in a few places in order to keep in sync.
   */
  @belongsTo('flow', { async: true, inverse: null }) declare flow: AsyncBelongsTo<FlowModel> | null;
  @belongsTo('flow-version', { async: true, inverse: null })
  declare flowVersion: AsyncBelongsTo<FlowVersionModel>;
  @belongsTo('parent-invite-context', { async: false, inverse: null })
  declare parentInviteContext: AsyncBelongsTo<ParentInviteContextModel>;
  @belongsTo('group-invite', { async: false, inverse: 'invites' }) declare groupInvite: GroupInviteModel;
  @belongsTo('visitor-profile', { async: false, inverse: null }) declare visitorProfile: VisitorProfileModel;

  @hasMany('employee', { async: false, inverse: null }) declare additionalHosts:
    | AsyncHasMany<EmployeeModel>
    | EmployeeModel[];
  @hasMany('location', { async: false, inverse: null }) declare childInviteLocations?:
    | AsyncHasMany<LocationModel>
    | LocationModel[]
    | null;
  @hasMany('notification-log', { async: false, inverse: null })
  declare multiTenancyVisitorNotificationLogs: SyncHasMany<NotificationLogModel>;
  @hasMany('platform-job', { async: true, inverse: 'invite' }) declare platformJobs: AsyncHasMany<PlatformJobModel>;
  @hasMany('questionnaire-response', { async: false, inverse: null })
  declare questionnaireResponses: SyncHasMany<QuestionnaireResponse>;
  @belongsTo('questionnaire-response-summary', { async: false, inverse: null })
  declare questionnaireResponseSummary: QuestionnaireResponseSummary | null;
  @hasMany('reservation', { async: true, inverse: 'invite' }) declare reservations: AsyncHasMany<ReservationModel>;
  @hasMany('visitor-document', { async: false, inverse: 'invites' }) declare visitorDocuments:
    | SyncHasMany<VisitorDocumentModel>
    | RecordArray<VisitorDocumentModel>
    | NativeArray<VisitorDocumentModel>;

  @attr('boolean', { defaultValue: false }) declare preregistrationComplete: boolean;

  @attr('string') declare fullName: string;
  @attr('string') declare email: string;
  @attr('string') declare inviterName: string;
  @attr('date') declare expectedArrivalTime: Date;
  @attr('date') declare endTime: Date;
  @attr('date') declare checkedInAt: Date;
  @attr('string') declare recurringRule: string | null;
  @attr('string') declare rruleUntil: string | null;
  @attr('string') declare privateNotes: string;
  @attr('string') declare propertyNotes: string;
  @attr('boolean') declare beenHereBefore: boolean;
  @attr('number', { defaultValue: () => 0 }) declare additionalGuests: number;
  @attr('boolean', { defaultValue: false }) declare skipGuestNotification: boolean;
  @attr() declare nda: unknown;
  @attr() declare guestUpdatedAt: string;
  @attr('string') declare agreementsStatus: AgreementsStatus;
  @attr() declare originalNdaSignDate: unknown;
  @attr() declare ndaAvailable: unknown;
  @attr('boolean') declare ndaDiscarded: boolean;
  @attr('locality', { defaultValue: () => ({ placeId: '' }) }) declare locality: string;
  @attr('string') declare flowName: string;
  @attr('string') declare groupName: string;
  @attr('boolean', { defaultValue: false }) declare employeeScreeningFlow: boolean;
  @attr('boolean') declare arrived: boolean;
  @attr('userData', { defaultValue: () => [] }) declare userData: NativeArray<UserDatum>;
  @attr('string') declare photoUrl: string;
  @attr('boolean', { defaultValue: false }) declare deletePhoto: boolean;
  @attr('array', { defaultValue: () => [] }) declare locationNames: string[];
  @attr('date') declare createdAt: Date;
  /**
   * create an entry to sign in the invite upon creation
   */
  @attr('boolean') declare signInOnCreate: boolean;

  @attr('date') declare anonymizedAt: Date | null;

  get visitorDocumentsLoaded(): boolean {
    if (<boolean>(<unknown>this.isNew)) return true;
    return this._visitorDocumentsLoaded;
  }
  set visitorDocumentsLoaded(value: boolean) {
    this._visitorDocumentsLoaded = value;
  }

  @tracked userDataValidationResult: UserDataValidationResult = VALID_USER_DATA_NO_ERRORS;
  @tracked _visitorDocumentsLoaded = false;

  get hasAdditionalGuests(): boolean {
    return this.additionalGuests > 0;
  }

  /**
   * Screener results and history for this invite
   */
  @belongsTo('visit-approval', { async: false, inverse: null })
  declare visitApproval: VisitApproval | null;

  get approvalWasDenied(): boolean {
    return this.currentApprovalStatus === 'denied';
  }
  get approved(): boolean {
    return this.currentApprovalStatus === 'approved';
  }
  get pending(): boolean {
    return this.currentApprovalStatus === 'pending';
  }
  get needsApprovalReview(): boolean {
    return this.currentApprovalStatus === 'review';
  }
  @alias('employeeScreeningFlow') isFromEmployeeScreening!: this['employeeScreeningFlow'];
  @alias('walkUpFlow') isFromWalkUp!: this['walkUpFlow'];

  // SQ-4574: Invert boolean in model to set same value in component
  @not('skipGuestNotification') sendGuestEmail!: boolean;

  get phoneNumbers(): string[] {
    return extractPhoneNumber(this);
  }

  get currentApprovalStatus(): ApprovalStatus | undefined {
    // entry is loaded
    if (this.entry?.content) {
      if (this.entry.content.visitApproval) return this.entry.content.visitApproval.prioritizedStatus;
    }

    return this.visitApproval?.prioritizedStatus;
  }

  @computed('currentLocation.location.employeeScreeningFlow', 'flow', 'flowName', 'isDestroyed', 'isDestroying')
  get walkUpFlow(): boolean {
    // This attribute seems to still get read by the `<Invites::SinglePrereg>` component during its teardown (in tests, at least).
    // The result of that is that this getter causes an error:
    // > Attempted to call store.peekAll(), but the store instance has already been destroyed
    // By bailing out if this is destroying/destroyed we avoid the error.
    if (this.isDestroying || this.isDestroyed) return false;

    // Refactor this once we can pull `PROPERTY_WALKUP` flow from the API.
    const localEmployeeScreeningFlow =
      this.store.peekAll('flow').findBy('employeeCentric', true) ||
      this.currentLocation.location?.employeeScreeningFlow;

    const isWalkup =
      localEmployeeScreeningFlow &&
      'type' in localEmployeeScreeningFlow &&
      localEmployeeScreeningFlow?.type === FLOW_TYPE.PROPERTY_WALKUP;

    return isWalkup || this.flowName === 'Property walk-up';
  }

  @computed('userData.@each.field', 'entry.userData.@each.field', 'inviterName')
  get host(): string {
    // eslint-disable-next-line ember/no-get
    const userData = this.entry ? <NativeArray<UserDatum>>get(this.entry, 'userData') || this.userData : this.userData;
    const hostField = userData.findBy('field', 'Host');
    // If there isn't any data inside the `userData` we will fallback it to the `inviterName`
    return hostField && isPresent(hostField.value) ? hostField.value : this.inviterName;
  }

  // eslint-disable-next-line ember/require-computed-property-dependencies
  @computed('platformJobs.@each.{pluginCategory,status}')
  get categorizedPlatformJobs(): Promise<Record<string, PlatformJobModel[]>> {
    if (this.platformJobs.isDestroyed) return Promise.resolve({});

    return this.platformJobs.then((platformJobs) => {
      return platformJobs.reduce<Record<string, PlatformJobModel[]>>((categorizedJobs, job) => {
        const category = job.pluginCategory;

        if (category && category !== 'nda') {
          categorizedJobs[category] = (categorizedJobs[category] || []).concat(job);
        }

        return categorizedJobs;
      }, {});
    });
  }

  // eslint-disable-next-line ember/require-computed-property-dependencies
  @computed('platformJobs.@each.{pluginCategory,status}')
  get ndaPlatformJobs(): Promise<Record<string, PlatformJobModel[]>> {
    if (this.platformJobs.isDestroyed) return Promise.resolve({});

    return this.platformJobs.then((platformJobs) => {
      return platformJobs.reduce<Record<string, PlatformJobModel[]>>((ndaJobs, job) => {
        const category = job.pluginCategory;

        if (category && category === 'nda') {
          ndaJobs[job.pluginName] = (ndaJobs[job.pluginName] || []).concat(job);
        }

        return ndaJobs;
      }, {});
    });
  }

  get needsLocationApprovalReview(): boolean {
    return needsLocationApprovalReview(this);
  }

  get needsPropertyApprovalReview(): boolean {
    return needsPropertyApprovalReview(this);
  }

  @filterBy('multiTenancyVisitorNotificationLogs', 'successful') successfulLogs!: NotificationLogModel[];
  @sortBy('successfulLogs', 'createdAt:desc') sortedSuccessfulLogs!: NotificationLogModel[];

  get latestNotificationLog(): NotificationLogModel | undefined {
    return this.sortedSuccessfulLogs[0];
  }

  get visitorEmail(): string {
    // eslint-disable-next-line ember/no-get
    const providedEmail = <string>get(this, 'entry.email');

    return providedEmail || this.email;
  }

  get groupInviteId(): string {
    // this needs to be explicitly typed here for some reason
    return (<Invite>this).belongsTo('groupInvite').id();
  }

  get isInGroupInvite(): boolean {
    // If this getter is called on a destroyed/destroying model instance (which happens in some tests, but not in the real app)
    // the call to `groupInviteId` (in particular its call to `.belongsTo()`) triggers an asserttion failure:
    // Assertion Failed: <model::invite:1> is not a record instantiated by @ember-data/store
    if (this.isDestroying || this.isDestroyed) return false;

    return Boolean(this.groupInviteId);
  }

  get visitorProfileId(): string | undefined {
    // belongsTo().id() returns the relationship id without touching the sync
    // belongsTo (which would throw if the record isn't loaded in the store).
    // @ts-expect-error - DT types for sync `belongsTo` don't expose .id() (see
    // app/models/questionnaire.ts:62). Works at runtime. Remove this directive
    // once @types/ember-data ships the BelongsToReference#id() overload.
    return this.belongsTo('visitorProfile').id() ?? undefined;
  }

  get isUserDataValid(): boolean {
    return this.userDataValidationResult?.isValid ?? true;
  }

  /**
   * approves all the screeners flagged for review
   */
  approve(screeners?: ReportSource[]): Promise<Invite | undefined> {
    return this.review('approve', screeners);
  }

  /**
   * denies all the screeners flagged for review
   */
  deny(screeners?: ReportSource[]): Promise<Invite | undefined> {
    return this.review('deny', screeners);
  }

  async printBadge(): Promise<unknown> {
    // If we are signed in, reprint the badge otherwise preprint it
    const entry = await this.entry;
    if (entry && entry.signInTime) {
      return entry.reprintBadge();
    } else {
      return this.ajaxFetch.request(urlBuilder.v1.printBadgesUrl(this.currentLocation.location.id), {
        type: 'POST',
        data: {
          invite_ids: [this.id],
        },
      });
    }
  }

  notifyInvite(data: PlainObject<unknown> | undefined): Promise<void> {
    return this.ajaxFetch.request(urlBuilder.v3.invites.sendNotification(this.id), {
      contentType: 'application/json',
      data,
      type: 'POST',
    });
  }

  visitorDocumentForTemplate(userDocumentTemplate: UserDocumentTemplateModel): VisitorDocumentModel | undefined {
    // eslint-disable-next-line ember/use-ember-get-and-set
    return this.visitorDocumentForIdentifier(userDocumentTemplate.get('identifier'));
  }

  visitorDocumentForIdentifier(identifier: Identifier): VisitorDocumentModel | undefined {
    if (!this.visitorDocumentsLoaded) return undefined;
    return this.visitorDocuments.find((visitorDocument) => visitorDocument.identifier === identifier);
  }

  /**
   * Validates userData values against their corresponding sign-in field options
   * for dropdown single-selection fields
   * @returns {Promise<{ isValid: boolean, errors: Array<{ field: string, message: string }> }>}
   */
  async validateUserData(): Promise<UserDataValidationResult> {
    this.userDataValidationResult = await this.#validateUserData();

    return this.userDataValidationResult;
  }

  async #validateUserData(): Promise<UserDataValidationResult> {
    const errors: UserDataValidationResult['errors'] = [];

    const flow = await this.flow;
    if (!flow) return VALID_USER_DATA_NO_ERRORS;

    const signInFieldPage = await flow.signInFieldPage;
    if (!signInFieldPage) return VALID_USER_DATA_NO_ERRORS;

    const signInFields = await signInFieldPage.signInFields;
    if (!signInFields) return VALID_USER_DATA_NO_ERRORS;

    // NOTE: the API will validate user data even if it belongs to a
    // sign-in field that is conditionally hidden in the UI.
    for (const userDatum of this.userData) {
      const { field, value } = userDatum;

      if (!(field && value)) continue;

      const signInField = signInFields.find((f) => f.name === field);
      if (!(signInField && signInField.isSingleSelection)) continue;
      if (isRemovedString(value)) continue;

      const options = signInField.options;
      if (!options || options.length === 0) continue;

      const validOptionValues = new Set(options.map((option) => option.value));
      const isValidOption = validOptionValues.has(value);

      if (!isValidOption) {
        errors.push({
          field,
          message: "Some of this invite's responses have been removed or changed. Please review and update.",
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  async review(action: 'approve' | 'deny', screeners?: ReportSource[]): Promise<Invite | undefined> {
    const response = await apiAction<SingleResponse<Invite>>(this, {
      method: 'POST',
      path: 'review?include=visit-approval,visit-approval.screener-results,visit-approval.screener-reviews',
      data: {
        action,
        'screener-sources': screeners,
      },
    });

    const serializer = this.store.serializerFor('invite');
    const normalized = serializer.normalizeSingleResponse(
      this.store,
      // @ts-expect-error
      Invite,
      response,
      response.data.id,
      'findRecord',
    );

    return <Invite>this.store.push(normalized);
  }

  declare agreeableNdas: SyncHasMany<AgreeableNdaModel>;
}

export type InviteWithVisitApproval = Invite & { visitApproval: VisitApproval };

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    invite: Invite;
  }
}
